import { Amplify } from 'aws-amplify'
import { fetchAuthSession } from 'aws-amplify/auth'
import { KeyValueStorageInterface } from 'aws-amplify/utils'
import { createUserPoolsTokenProvider } from 'aws-amplify/adapter-core'

import { AuthTokens } from 'common/types/responses'

function decodeJwtPayload(token?: string): any {
  if (!token) return {}
  try {
    const part = token.split('.')[1]
    const padLength = part.length % 4
    const padded = part + (padLength ? '='.repeat(4 - padLength) : '')
    const base64 = padded.replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(atob(base64))
  } catch (e) {
    return {}
  }
}

const AMPLIFY_PREFIX = 'CognitoIdentityServiceProvider'

const storage = typeof window !== 'undefined' ? window.localStorage : null

const tokenStorage: KeyValueStorageInterface = {
  async clear() {
    const { userPoolWebClientId } = Cognito.config
    if (!userPoolWebClientId) return

    const lastAuthUserKey = `${AMPLIFY_PREFIX}.${userPoolWebClientId}.LastAuthUser`
    const username = storage?.getItem(lastAuthUserKey) || null

    const keysToRemove = [
      lastAuthUserKey,
      ...(username
        ? [
            `${AMPLIFY_PREFIX}.${userPoolWebClientId}.${username}.accessToken`,
            `${AMPLIFY_PREFIX}.${userPoolWebClientId}.${username}.idToken`,
            `${AMPLIFY_PREFIX}.${userPoolWebClientId}.${username}.refreshToken`,
            `${AMPLIFY_PREFIX}.${userPoolWebClientId}.${username}.clockDrift`,
            `${AMPLIFY_PREFIX}.${userPoolWebClientId}.${username}.userData`,
          ]
        : []),
    ]

    keysToRemove.forEach((key) => storage?.removeItem(key))
  },

  async getItem(key: string): Promise<string | null> {
    return storage?.getItem(key) || null
  },

  async removeItem(key: string): Promise<void> {
    storage?.removeItem(key)
  },

  async setItem(key: string, value: string): Promise<void> {
    storage?.setItem(key, value)
  },
}

interface CognitoConfig {
  region?: string
  userPoolId: string
  userPoolWebClientId: string
}

const Cognito = {
  config: {} as CognitoConfig,

  createSession(accessToken: string, idToken: string) {
    const idPayload = decodeJwtPayload(idToken)
    return {
      getAccessToken: () => ({ getJwtToken: () => accessToken }),
      getIdToken: () => ({ getJwtToken: () => idToken, payload: idPayload }),
    }
  },

  getSession: async function (): Promise<any> {
    const session = await fetchAuthSession()
    const tokens = session.tokens
    if (tokens?.accessToken) {
      return this.createSession(
        String(tokens.accessToken),
        tokens.idToken ? String(tokens.idToken) : '',
      )
    }
    throw new Error('UserUnauthenticatedException')
  },

  init: (config: CognitoConfig) => {
    Cognito.config = config

    const authConfig = {
      Cognito: {
        userPoolClientId: config.userPoolWebClientId,
        userPoolId: config.userPoolId,
      },
    }

    const customTokenProvider = createUserPoolsTokenProvider(
      authConfig,
      tokenStorage,
    )

    Amplify.configure(
      { Auth: authConfig },
      { Auth: { tokenProvider: customTokenProvider } },
    )
  },

  loginWithToken: async function (
    username: string,
    data: AuthTokens,
  ): Promise<void> {
    const userPoolWebClientId = Cognito.config?.userPoolWebClientId
    if (!userPoolWebClientId) return

    const prefix = `${AMPLIFY_PREFIX}.${userPoolWebClientId}`

    await Promise.all([
      tokenStorage.setItem(`${prefix}.${username}.idToken`, data.id_token),
      tokenStorage.setItem(`${prefix}.${username}.accessToken`, data.token),
      tokenStorage.setItem(
        `${prefix}.${username}.refreshToken`,
        data.refresh_token,
      ),
      tokenStorage.setItem(`${prefix}.LastAuthUser`, username),
    ])
  },

  logout: async function () {
    await tokenStorage.clear()
  },
}

export default Cognito
